import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  Title, Tooltip, Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { RequireRole } from '../../components/CallcenterV2/RequireRole';
import KpiGrid from '../../components/CallcenterV2/KpiGrid';
import RealtimeBadge from '../../components/CallcenterV2/RealtimeBadge';
import { fetchKpis, fetchQueueMetrics, fetchHistory } from '../../services/callcenterV2/metricsService';
import { fetchAgents, pauseAgent, unpauseAgent, forceEnableAgent, forceDisableAgent } from '../../services/callcenterV2/agentsService';
import { fetchQueues } from '../../services/callcenterV2/queuesService';
import '../../components/CallcenterV2/ccv2-components.css';
import './config-styles.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const PREF_KEY = 'ccv2_mgr_dash_prefs';
const DEFAULT_PERIOD = '7';
const DEFAULT_GRANULARITY = 'daily';

const PERIODS = [
  { value: '0', label: 'Hoje' },
  { value: '7', label: 'Últimos 7 dias' },
  { value: '30', label: 'Últimos 30 dias' },
  { value: '90', label: 'Últimos 90 dias' },
];

const GRANULARITIES = [
  { value: 'hourly', label: 'Por hora' },
  { value: 'daily', label: 'Por dia' },
];

const formatDuration = (s) => {
  if (!s || s <= 0) return '0s';
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
};

function DashboardManager() {
  const user = useCurrentUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kpis, setKpis] = useState({});
  const [queueMetrics, setQueueMetrics] = useState([]);
  const [historyData, setHistoryData] = useState([]);
  const [agents, setAgents] = useState([]);
  const [queues, setQueues] = useState([]);
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [granularity, setGranularity] = useState(DEFAULT_GRANULARITY);
  const [actionLoading, setActionLoading] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);
  const [queueFilter, setQueueFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [expandedAgent, setExpandedAgent] = useState(null);

  // Persist filters in localStorage
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(PREF_KEY) || '{}');
      if (saved.period) setPeriod(saved.period);
      if (saved.granularity) setGranularity(saved.granularity);
    } catch (_e) {}
  }, []);

  const savePref = useCallback((key, val) => {
    try {
      const saved = JSON.parse(localStorage.getItem(PREF_KEY) || '{}');
      localStorage.setItem(PREF_KEY, JSON.stringify({ ...saved, [key]: val }));
    } catch (_e) {}
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const days = parseInt(period, 10) || 0;
      const startDate = days === 0
        ? endDate
        : new Date(Date.now() - days * 86400000).toISOString().split('T')[0];

      const [kpisData, queueMData, histData, agentsData, queuesData] = await Promise.allSettled([
        fetchKpis(),
        fetchQueueMetrics(),
        fetchHistory({ startDate, endDate, granularity }),
        fetchAgents(),
        fetchQueues(),
      ]);

      if (kpisData.status === 'fulfilled') setKpis(kpisData.value || {});
      if (queueMData.status === 'fulfilled') setQueueMetrics(queueMData.value?.queues || []);
      if (histData.status === 'fulfilled') setHistoryData(histData.value?.data || []);
      if (agentsData.status === 'fulfilled') setAgents(Array.isArray(agentsData.value) ? agentsData.value : []);
      if (queuesData.status === 'fulfilled') setQueues(queuesData.value?.queues || []);
    } catch (e) {
      setError(e.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [period, granularity]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── KPI array for KpiGrid ────────────────────────────────────────────────────
  const kpiMetrics = useMemo(() => [
    {
      metricKey: 'service_level',
      label_pt: 'SLA',
      value: kpis.service_level ?? kpis.sla ?? null,
      unit: 'percent',
      target: 80,
      warningValue: 70,
      criticalValue: 60,
      higherIsBetter: true,
    },
    {
      metricKey: 'abandon_rate',
      label_pt: 'Abandono',
      value: kpis.abandon_rate ?? kpis.abandonRate ?? null,
      unit: 'percent',
      target: 5,
      warningValue: 8,
      criticalValue: 12,
      higherIsBetter: false,
    },
    {
      metricKey: 'asa',
      label_pt: 'TME (ASA)',
      value: kpis.asa ?? kpis.avgWaitTime ?? null,
      unit: 'seconds',
      target: 30,
      warningValue: 45,
      criticalValue: 60,
      higherIsBetter: false,
    },
    {
      metricKey: 'aht',
      label_pt: 'TMA (AHT)',
      value: kpis.aht ?? kpis.avgTalkTime ?? null,
      unit: 'seconds',
    },
    {
      metricKey: 'serviced_today',
      label_pt: 'Atendidas Hoje',
      value: kpis.serviced_today ?? kpis.servicedToday ?? null,
      unit: 'count',
    },
    {
      metricKey: 'abandoned_today',
      label_pt: 'Abandonadas Hoje',
      value: kpis.abandoned_today ?? kpis.abandonedToday ?? null,
      unit: 'count',
      higherIsBetter: false,
    },
  ], [kpis]);

  // ── History chart data ────────────────────────────────────────────────────────
  const { dates, labels, chartDataCalls, chartDataTimes } = useMemo(() => {
    const grouped = {};
    (historyData || []).forEach((row) => {
      const d = (row.date || '').split('T')[0];
      if (!grouped[d]) grouped[d] = { answered: 0, abandoned: 0, wait: 0, talk: 0, n: 0 };
      grouped[d].answered += row.calls_answered || 0;
      grouped[d].abandoned += row.calls_abandoned || 0;
      grouped[d].wait += row.total_wait_time || 0;
      grouped[d].talk += row.total_talk_time || 0;
      grouped[d].n++;
    });
    const ds = Object.keys(grouped).sort();
    const lbs = ds.map((d) => new Date(d + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }));
    return {
      dates: ds,
      labels: lbs,
      chartDataCalls: {
        labels: lbs,
        datasets: [
          { label: 'Atendidas', data: ds.map((d) => grouped[d].answered), borderColor: 'rgb(34,197,94)', backgroundColor: 'rgba(34,197,94,0.4)', tension: 0.3 },
          { label: 'Abandonadas', data: ds.map((d) => grouped[d].abandoned), borderColor: 'rgb(239,68,68)', backgroundColor: 'rgba(239,68,68,0.4)', tension: 0.3 },
        ],
      },
      chartDataTimes: {
        labels: lbs,
        datasets: [
          { label: 'TME Médio (s)', data: ds.map((d) => grouped[d].n > 0 ? Math.round(grouped[d].wait / grouped[d].n) : 0), borderColor: 'rgb(245,158,11)', backgroundColor: 'rgba(245,158,11,0.4)', tension: 0.3 },
          { label: 'TMA Médio (s)', data: ds.map((d) => grouped[d].n > 0 ? Math.round(grouped[d].talk / grouped[d].n) : 0), borderColor: 'rgb(59,130,246)', backgroundColor: 'rgba(59,130,246,0.4)', tension: 0.3 },
        ],
      },
    };
  }, [historyData]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'top' } },
    scales: { y: { beginAtZero: true } },
  }), []);

  // ── Agent actions ────────────────────────────────────────────────────────────
  const handleAgentAction = useCallback(async (agentKey, action) => {
    setActionLoading(`${agentKey}-${action}`);
    setActionMsg(null);
    try {
      if (action === 'pause') {
        await pauseAgent(agentKey, { pauseReasonId: 0, notes: 'Pausa pelo gestor' });
        setActionMsg('Agente pausado com sucesso.');
      } else if (action === 'unpause') {
        await unpauseAgent(agentKey);
        setActionMsg('Agente despausado com sucesso.');
      } else if (action === 'force-enable') {
        await forceEnableAgent(agentKey, { reason: 'Forçado pelo gestor' });
        setActionMsg('Escala forçada ON.');
      } else if (action === 'force-disable') {
        await forceDisableAgent(agentKey, { reason: 'Forçado pelo gestor' });
        setActionMsg('Escala forçada OFF.');
      }
      setTimeout(() => setActionMsg(null), 3000);
    } catch (e) {
      setActionMsg(`Erro: ${e.message}`);
    } finally {
      setActionLoading(null);
    }
  }, []);

  // ── Filtered lists ────────────────────────────────────────────────────────────
  const filteredQueues = useMemo(() =>
    queues.filter((q) => !queueFilter || (q.name || q.queueKey || '').toLowerCase().includes(queueFilter.toLowerCase())),
    [queues, queueFilter],
  );

  const filteredAgents = useMemo(() =>
    agents.filter((a) =>
      !agentFilter || (a.displayName || a.agentKey || '').toLowerCase().includes(agentFilter.toLowerCase()),
    ),
    [agents, agentFilter],
  );

  const queueBarData = useMemo(() => ({
    labels: queueMetrics.slice(0, 10).map((q) => q.queue_name || q.queue_key || '?'),
    datasets: [
      { label: 'Atendidas', data: queueMetrics.slice(0, 10).map((q) => q.calls_answered || 0), backgroundColor: 'rgba(34,197,94,0.75)' },
      { label: 'Abandonadas', data: queueMetrics.slice(0, 10).map((q) => q.calls_abandoned || 0), backgroundColor: 'rgba(239,68,68,0.75)' },
    ],
  }), [queueMetrics]);

  return (
    <RequireRole roles={['manager', 'admin', 'root']} redirectTo="/callcenterV2/dashboard/agent">
      <div className="ccv2-page-container ccv2-p5-container">
        {/* ── Header ── */}
        <div className="ccv2-page-header">
          <div className="ccv2-page-title-area">
            <h1>Dashboard do Gestor</h1>
            <p>Visão completa do tenant · métricas, filas e agentes</p>
          </div>
          <div className="ccv2-page-actions" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
            <select className="ccv2-period-select ccv2-p5-select" value={period} onChange={(e) => { setPeriod(e.target.value); savePref('period', e.target.value); }}>
              {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <select className="ccv2-period-select ccv2-p5-select" value={granularity} onChange={(e) => { setGranularity(e.target.value); savePref('granularity', e.target.value); }}>
              {GRANULARITIES.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
            <button className="ccv2-p5-btn ccv2-p5-btn-secondary" onClick={loadAll} disabled={loading}>
              {loading ? '⌛' : '🔄'} Atualizar
            </button>
          </div>
        </div>

        {error && (
          <div className="ccv2-p5-error">
            ⚠️ {error}
            <button onClick={loadAll} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}>Tentar novamente</button>
          </div>
        )}

        {actionMsg && (
          <div className={`ccv2-p5-${actionMsg.startsWith('Erro') ? 'error' : 'success'}`}>
            {actionMsg}
          </div>
        )}

        {loading ? (
          <div className="ccv2-config-loading">
            <div className="ccv2-spinner" />
            <p>Carregando dados do dashboard...</p>
          </div>
        ) : (
          <>
            {/* ── KPI Row ── */}
            <div className="ccv2-p5-section">
              <div className="ccv2-p5-section-title">📊 Indicadores do Tenant</div>
              <KpiGrid metrics={kpiMetrics} />
            </div>

            {/* ── Charts ── */}
            <div className="ccv2-dashboard-grid">
              <div className="ccv2-dashboard-card ccv2-dashboard-card-large">
                <h3>Chamadas por {granularity === 'hourly' ? 'Hora' : 'Dia'}</h3>
                <div className="ccv2-chart-container">
                  {labels.length > 0 ? <Line data={chartDataCalls} options={chartOptions} /> : <div className="ccv2-p5-empty">Sem dados no período selecionado.</div>}
                </div>
              </div>

              <div className="ccv2-dashboard-card ccv2-dashboard-card-large">
                <h3>TME / TMA por {granularity === 'hourly' ? 'Hora' : 'Dia'}</h3>
                <div className="ccv2-chart-container">
                  {labels.length > 0 ? <Line data={chartDataTimes} options={chartOptions} /> : <div className="ccv2-p5-empty">Sem dados no período selecionado.</div>}
                </div>
              </div>

              {queueMetrics.length > 0 && (
                <div className="ccv2-dashboard-card ccv2-dashboard-card-large">
                  <h3>Chamadas por Fila (top 10)</h3>
                  <div className="ccv2-chart-container">
                    <Bar data={queueBarData} options={chartOptions} />
                  </div>
                </div>
              )}
            </div>

            {/* ── Queues Table ── */}
            <div className="ccv2-p5-section">
              <div className="ccv2-p5-section-title">🔀 Filas</div>
              <div className="ccv2-p5-filters">
                <input className="ccv2-p5-input" placeholder="Filtrar fila..." value={queueFilter} onChange={(e) => setQueueFilter(e.target.value)} style={{ maxWidth: 220 }} />
              </div>
              {filteredQueues.length === 0 ? (
                <div className="ccv2-p5-empty">Nenhuma fila encontrada.</div>
              ) : (
                <div className="ccv2-config-card">
                  <div className="ccv2-config-table-wrapper">
                    <table className="ccv2-config-table">
                      <thead>
                        <tr>
                          <th>Fila</th>
                          <th>Estratégia</th>
                          <th>Timeout</th>
                          <th>Wrapup</th>
                          <th>SLA Threshold</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredQueues.map((q) => (
                          <tr key={q.id_callcenter_queue || q.queueKey}>
                            <td className="ccv2-cell-name">{q.name || q.queueKey}</td>
                            <td><code>{q.strategy || '—'}</code></td>
                            <td>{q.timeout ?? '—'}s</td>
                            <td>{q.wrapupTime ?? '—'}s</td>
                            <td>{q.serviceLevelThreshold ?? '—'}s</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* ── Agents Table ── */}
            <div className="ccv2-p5-section">
              <div className="ccv2-p5-section-title">👤 Agentes</div>
              <div className="ccv2-p5-filters">
                <input className="ccv2-p5-input" placeholder="Filtrar agente..." value={agentFilter} onChange={(e) => setAgentFilter(e.target.value)} style={{ maxWidth: 220 }} />
              </div>
              {filteredAgents.length === 0 ? (
                <div className="ccv2-p5-empty">Nenhum agente encontrado.</div>
              ) : (
                <div className="ccv2-config-card">
                  <div className="ccv2-config-table-wrapper">
                    <table className="ccv2-config-table">
                      <thead>
                        <tr>
                          <th>Agente</th>
                          <th>Ramal</th>
                          <th>E-mail</th>
                          <th>Skills</th>
                          <th>Ações (Gestor)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAgents.map((a) => {
                          const key = a.agentKey || a.id_callcenter_agent;
                          const isLoading = actionLoading?.startsWith(String(key));
                          return (
                            <tr key={key}>
                              <td className="ccv2-cell-name">{a.displayName || a.agentKey}</td>
                              <td><code>{a.extension || '—'}</code></td>
                              <td style={{ fontSize: '0.8rem', color: '#64748b' }}>{a.email || '—'}</td>
                              <td style={{ fontSize: '0.78rem' }}>{(a.skills || []).slice(0, 3).join(', ') || '—'}</td>
                              <td>
                                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                                  <button className="ccv2-p5-btn ccv2-p5-btn-warning" style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                                    disabled={isLoading} onClick={() => handleAgentAction(key, 'pause')} title="Forçar pausa">
                                    ⏸ Pausar
                                  </button>
                                  <button className="ccv2-p5-btn ccv2-p5-btn-success" style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                                    disabled={isLoading} onClick={() => handleAgentAction(key, 'unpause')} title="Tirar pausa">
                                    ▶ Despausar
                                  </button>
                                  <button className="ccv2-p5-btn ccv2-p5-btn-primary" style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                                    disabled={isLoading} onClick={() => handleAgentAction(key, 'force-enable')} title="Forçar on na escala">
                                    ✅ Escala On
                                  </button>
                                  <button className="ccv2-p5-btn ccv2-p5-btn-secondary" style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                                    disabled={isLoading} onClick={() => handleAgentAction(key, 'force-disable')} title="Forçar off na escala">
                                    🚫 Escala Off
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* ── Audit link ── */}
            <div style={{ textAlign: 'right', marginBottom: '1rem' }}>
              <a href="/callcenterV2/audit" style={{ fontSize: '0.85rem', color: '#4A7BA7' }}>
                📋 Ver log de auditoria →
              </a>
            </div>
          </>
        )}
      </div>
    </RequireRole>
  );
}

export default DashboardManager;
